import React, { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { queryCache } from "react-query";
import CustomInputField from "../../../../CustomHTMLElements/CustomInputField";
import { TextEditor } from "../../../../../helpers/TextEditor";
import { Button, Divider, message, Space, Typography } from "antd";
import { errorHandler } from "../../../../../helpers/errorHandler";
import * as ajaxEndpoints from "../../../../../api/ajax-endpoints";
import { patchData, postData } from "../../../../../newApis/dotNetApiMethods";
import { AxiosResponse } from "axios";

const schema = yup.object().shape({
  title: yup.string().trim().required("Please enter title"),
  editorContent: yup
    .string()
    .test("not-empty", "Please enter terms and conditions", (value) => {
      if (!value) return false;
      const text = value.replace(/<(.|\n)*?>/g, "").trim();
      return text.length > 0;
    }),
  certificateTermsContent: yup
    .string()
    .test(
      "not-empty",
      "Please enter certificate terms and conditions",
      (value) => {
        if (!value) return false;
        const text = value.replace(/<(.|\n)*?>/g, "").trim();
        return text.length > 0;
      },
    ),
});

type FormData = {
  title: string;
  editorContent: string;
  certificateTermsContent: string;
};

const CreateTermsAndConditionsModal = ({ onCloseModal, dataRecord }: any) => {
  const [isCreating, setIsCreating] = useState(false);
  const isEditing = Boolean(dataRecord);
  const { Title, Text } = Typography;

  const { register, handleSubmit, errors, setValue } = useForm({
    resolver: yupResolver(schema),
  });

  const handleCreatEditTermsAndCondition = async (values: FormData) => {
    setIsCreating(true);
    const reqBody = {
      title: values?.title,
      termsAndConditions: values?.editorContent,
      certificateTermAndCondition: values?.certificateTermsContent,
    };

    let response: AxiosResponse<any, any>;
    try {
      if (!dataRecord) {
        response = await postData(
          ajaxEndpoints.DB_TERMS_AND_CONDITIONS,
          reqBody,
        );
      } else {
        response = await patchData(ajaxEndpoints.DB_TERMS_AND_CONDITIONS, {
          ...reqBody,
          id: dataRecord?.id,
        });
      }

      message.success(response?.data?.message);
      queryCache.invalidateQueries("termsAndConditions");

      setTimeout(() => {
        onCloseModal();
      }, 2000);
    } catch (error) {
      message.error(errorHandler(error));
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditorChange = (text: string) => {};

  useEffect(() => {
    setValue("title", dataRecord?.title || "");
    setValue("editorContent", dataRecord?.termsAndConditions || "");
    setValue(
      "certificateTermsContent",
      dataRecord?.certificateTermAndCondition || "",
    );
  }, [dataRecord, setValue]);

  return (
    <div
      className="Modal__dialog lg-modal animated slideInDown"
      role="document"
    >
      <div className="LoanModal Modal__content">
        <div className="Modal__body" style={{ overflow: "hidden" }}>
          <React.Fragment>
            <div className="d-flex align-items-start justify-content-between">
              <div>
                <Title level={4} className="m-b-0">
                  {isEditing
                    ? "Edit Terms & Conditions"
                    : "Create New Terms & Conditions"}
                </Title>
                <Text type="secondary">
                  Write terms in plain language. Use short sections, headings,
                  and bullet points so investors can quickly find key clauses.
                </Text>
              </div>
            </div>

            <Divider className="m-y-3" />

            <div className="bg-white rounded shadow-sm p-4">
              <form onSubmit={handleSubmit(handleCreatEditTermsAndCondition)}>
                <div className="row">
                  <div className="col-12">
                    <CustomInputField
                      label="Title"
                      type="text"
                      name="title"
                      placeholder="e.g. Advancly Investment Terms"
                      defaultValue={dataRecord?.title}
                      reference={register}
                      errors={errors.title}
                      labelClass="mb-2"
                      customInputStyle={{ height: "44px" }}
                    />

                    <div className="w-100 mb-4">
                      <Text className="label-txt">
                        Terms and conditions content
                      </Text>
                      <TextEditor
                        onChange={handleEditorChange}
                        defaultValue={
                          dataRecord?.termsAndConditions
                            ? dataRecord?.termsAndConditions
                            : ""
                        }
                        register={register}
                        setValue={setValue}
                        errors={errors}
                      />
                    </div>

                    <div className="w-100">
                      <Text className="label-txt">
                        Investment certificate terms and conditions content
                      </Text>
                      <TextEditor
                        onChange={handleEditorChange}
                        defaultValue={
                          dataRecord?.certificateTermAndCondition
                            ? dataRecord?.certificateTermAndCondition
                            : ""
                        }
                        register={register}
                        setValue={setValue}
                        errors={errors}
                        fieldName="certificateTermsContent"
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-3">
                  <Space size={12}>
                    <Button
                      htmlType="button"
                      onClick={onCloseModal}
                      size="large"
                    >
                      <i className="fa fa-times" /> Cancel
                    </Button>

                    <Button
                      htmlType="submit"
                      type="primary"
                      size="large"
                      loading={isCreating}
                    >
                      <i className="fa fa-check-circle" />{" "}
                      {isEditing ? "Update" : "Create"}
                    </Button>
                  </Space>
                </div>
              </form>
            </div>
          </React.Fragment>
        </div>
      </div>
    </div>
  );
};

export default CreateTermsAndConditionsModal;
